import React, { useState, useEffect } from 'react';
import adService from '../../services/adService';

export interface AdBannerProps {
  id: string;
  className?: string;
  slot?: 'header' | 'sidebar' | 'infeed' | 'footer';
  size?: 'leaderboard' | 'rectangle' | 'skyscraper' | 'mobile';
  fallbackImage?: string;
  category?: string;
}

const AdBanner: React.FC<AdBannerProps> = ({ 
  id, 
  className = '',
  slot = 'infeed',
  size = 'rectangle', 
  fallbackImage,
  category
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [adConfig, setAdConfig] = useState<any>(null);
  
  // Get ad configuration on component mount
  useEffect(() => {
    const config = adService.getAdConfig(slot, size, { category });
    setAdConfig(config);
    
    if (config) {
      // Track impression
      adService.trackAdImpression(id, slot, window.location.pathname);
      
      // Simulate ad loading
      const timeout = setTimeout(() => {
        setIsLoaded(true);
      }, 500);
      
      return () => clearTimeout(timeout);
    }
  }, [id, slot, size, category]);

  // Don't render anything if no ad is available or user is premium
  if (adService.isPremiumUser() || !adConfig) {
    return null;
  }

  // Determine classes based on size
  const sizeClasses = {
    leaderboard: 'w-full h-[90px] sm:h-[90px]', // 728×90
    rectangle: 'w-full max-w-[336px] h-[280px]', // 336×280
    skyscraper: 'hidden lg:block w-[160px] h-[600px]', // 160×600
    mobile: 'w-full h-[100px] sm:h-[90px]' // Mobile optimized
  };

  const handleClick = () => {
    if (adConfig && adConfig.targetUrl) {
      adService.trackAdClick(id, slot, adConfig.targetUrl, window.location.pathname);
    }
  };

  const imageUrl = adConfig?.imageUrl || fallbackImage;

  return (
    <div 
      id={`ad-${id}-${slot}`} 
      className={`bg-neutral-100 border border-neutral-200 rounded-lg overflow-hidden flex items-center justify-center ${sizeClasses[size]} ${className} transition-opacity ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      data-ad-slot={slot}
      data-ad-size={size}
      onClick={handleClick}
      aria-label="Advertisement"
    >
      {imageUrl ? (
        <a 
          href={adConfig?.targetUrl || '#'} 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-full h-full block"
        >
          <img 
            src={imageUrl} 
            alt="Advertisement" 
            className="w-full h-full object-cover"
            onError={() => setHasError(true)}
          />
        </a>
      ) : (
        <div className="text-center p-2">
          <p className="text-xs text-neutral-400 uppercase tracking-wide">Advertisement</p>
          <div className="border border-dashed border-neutral-300 rounded p-2 mt-2">
            <p className="text-neutral-500 text-sm">{size} Ad Space</p>
            <p className="text-neutral-400 text-xs mt-1">{id}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdBanner;